// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { SmartSort, SmartSortProduct } from '@iheartjane/dm-sdk';
import { Typography, useToast } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
import type { Store } from '@jane/shared/types';

import { useGetCart } from '../../../data-access';
import type { RootLoaderType } from '../../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../../hooks';
import { horizontallyScrollable } from '../../../util';
import { Grid } from '../Grid/Grid';
import { ProductAddedToast } from '../ProductAddedToast/ProductAddedToast';
import { MenuProductCard } from '../ProductCard/MenuProductCard';
import { productCardMaxWidth } from '../ProductCard/ProductCard';
import { ProductCardSkeleton } from '../ProductCard/ProductCardSkeleton';
import { ProductDetailsSection } from '../ProductDetailsSection/ProductDetailsSection';

const NUM_PLACEHOLDER_PRODUCTS = 7;

const Container = styled(Grid)(() => {
  const { defaultSpacing } = useKioskSpacing({
    defaultPadSize: 48,
    defaultSize: 64,
  });

  return {
    ...horizontallyScrollable,
    paddingRight: defaultSpacing,
    paddingTop: '24px',
    gridAutoFlow: 'column', // Ensures items stay in a single row
    gridTemplateRows: '1fr', // Explicitly enforces a single row
    overflowX: 'auto' as const, // Enables horizontal scrolling if necessary
    whiteSpace: 'nowrap', // Prevents unintended wrapping
  };
});

const RenderPdpRowContainer = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 32,
  position: 'relative' as const,
});

const StyledProductCard = styled(MenuProductCard)({
  height: '100%',
});

const SponsoredLabel = styled.div({
  display: 'flex',
  justifyContent: 'left',
});

export interface PdpRowProps {
  isLoading: boolean;
  row: SmartSort<AlgoliaProduct> | undefined;
}

export const PdpRow = ({ isLoading = false, row }: PdpRowProps) => {
  const { getKioskSpacing } = useKioskSpacing();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: cart } = useGetCart();
  const { pathname } = useLocation();
  const toast = useToast();
  const goBack = useNavigateBack();
  const [queryParams] = useSearchParams();
  const pdpDepth = queryParams.get('pdpDepth');
  const numericPdpDepth = pdpDepth ? parseInt(pdpDepth) : 0;

  const usePDPRowNavigation = useCallback(
    (productId: number, priceId?: PriceId) => {
      const basePath = pathname.replace(/\/products\/\d+$/, '');

      const newQueryParams = new URLSearchParams({
        priceId: priceId ?? '',
      });
      newQueryParams.set('pdpDepth', (numericPdpDepth + 1).toString());

      return `${basePath}/products/${productId}?${newQueryParams.toString()}`;
    },
    [numericPdpDepth, pathname]
  );

  const isRowSponsored = useMemo(() => {
    if (!row || !row.products.length) {
      return false;
    }

    return (
      row.products.filter((product) => product.isSponsored).length ===
      row.products.length
    );
  }, [row]);

  const onQuickAddPdp = useCallback(() => {
    if (pathname.includes('cart')) {
      toast.add({
        label: <ProductAddedToast productCount={1} storeId={storeId} />,
      });
    }
    if (numericPdpDepth > 0) goBack(numericPdpDepth);
  }, [goBack, numericPdpDepth, pathname, storeId, toast]);

  const renderPdpRow = useCallback(
    (pdpRowProduct: SmartSortProduct<AlgoliaProduct>) => (
      <RenderPdpRowContainer key={`pdp-row-${pdpRowProduct.objectId}`}>
        <StyledProductCard
          highlightDeal
          product={pdpRowProduct}
          navigateTo={usePDPRowNavigation}
          store={store}
          cart={cart}
          isPdpProduct
          displaySponsoredLabel={!isRowSponsored}
          onPdpQuickAdd={onQuickAddPdp}
        />
      </RenderPdpRowContainer>
    ),
    [cart, isRowSponsored, onQuickAddPdp, store, usePDPRowNavigation]
  );

  if (!row || row.products.length === 0) {
    return null;
  }

  return (
    <ProductDetailsSection title={'You may also like'} isLoading={isLoading}>
      {!isLoading && isRowSponsored && (
        <SponsoredLabel>
          <Typography variant="mini" color="grays-mid" textAlign="center">
            Sponsored
          </Typography>
        </SponsoredLabel>
      )}
      <Container
        gap={getKioskSpacing(48, 40)}
        gridTemplateColumns={`repeat(${Math.max(
          NUM_PLACEHOLDER_PRODUCTS,
          row?.products.length
        )}, minmax(${productCardMaxWidth}px, 28%))`}
      >
        {isLoading &&
          Array(NUM_PLACEHOLDER_PRODUCTS)
            .fill(null)
            .map((_, index) => <ProductCardSkeleton key={index} />)}
        {!isLoading && row.products.map(renderPdpRow)}
      </Container>
    </ProductDetailsSection>
  );
};
